import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { PatientCallerTable } from "./components/PatientCallerTable";
import CRUDPatientCallerModal from "./CRUDPatientCallerModal";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import { useDispatch } from "react-redux";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import { PatientCaller } from "../../../../interface/interface";
import { removePatientCaller, savePatientCaller, resetAllNumberCallers } from "../../../../servicesHooks/Location/useLocationConfigService";
import useResizeListener from "../../../../hooks/useResizeListener";
import LocationEstablishmentFilter from "../components/LocationEstablishmentFilter";
import useEstablishmentFilterSelection from "../../hooks/useEstablishmentFilterSelection";

export default function PatientCallerView() {
    const isMobile = useResizeListener(768);
    const { establishments, orderedEstablishments, orderedActiveEstablishments, setEstablishments, setCallersToEstablishments, hasDuplicateAssociatedHospitalLocation } = useLocationConfigContext();
    const dispatch = useDispatch();

    const [openModal, setOpenModal] = useState(false);
    const [editCaller, setEditCaller] = useState<PatientCaller | null>(null);

    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const { selectedEstablishmentIds, setSelectedEstablishmentIds } = useEstablishmentFilterSelection(isMobile);

    const updateCallers = (estIndex: number, newCallers: PatientCaller[]) => { //Actualiza la lista de llamadores de un determinado establecimiento
        setEstablishments((prev) => {
            const updated = [...prev];
            updated[estIndex] = { ...updated[estIndex], callers: newCallers };
            return updated;
        });
    };

    const handleCreate = () => { //Crea el llamador, en este caso si aparece habilitado el Combobox de Establecimientos
        setEditCaller(null);
        setOpenModal(true);
    };

    const handleResetAll = () => {
        resetAllNumberCallers()
        .then(() => {
            dispatch(setNotification({ title: "Numeradores reseteados", desc: 'Se han reseteado todos los numeradores', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            setCallersToEstablishments(establishments);
        })
        .catch((err) => {
            console.error(err);
            dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo resetear los numeradores`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    const handleEdit = (caller: PatientCaller) => { //Edita un llamador en especifico, el cual se precargan sus datos en la modal. En este caso no aparece habilitado el Combobox de Establecimientos pues ya se sabe
        setEditCaller(caller);
        setOpenModal(true);
    };

    const handleOpenRemoveModal = (caller: PatientCaller) => {
        setEditCaller(caller) //se usa para eliminar en este flujo
        setOpenRemoveModal(true)
    }
    const handleDelete = (caller: PatientCaller) => { //Elimina el llamador
        removePatientCaller(caller.patientcallerconfig)
        .then(() => {
            dispatch(setNotification({ title: "Eliminación exitosa", desc: 'Se ha eliminado el llamador', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            setCallersToEstablishments(establishments);

            setEditCaller(null)
            setOpenRemoveModal(false)
        }).catch((err) => {
            console.error(err)
            if (err.response?.status === 409) {
                if (err.response?.data === 'ASSOCIATED_PLAYLISTELEMENTS')
                    dispatch(setNotification({ title: "No se puede eliminar", desc: "Debe desasociar los elementos multimedia antes de eliminar.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            } else
                dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo eliminar el llamador`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    const handleSave = (newCaller: PatientCaller) => { //Se ejecuta al confirmar los datos del caller centinela de la CRUDModal
        const estIndex = establishments.findIndex((e) => e.buildingid === newCaller.establishment);

        if (hasDuplicateAssociatedHospitalLocation(newCaller)) {
            dispatch(setNotification({ title: "No se puede guardar", desc: "No se pueden repetir los consultorios o puestos de atención asociados.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            return;
        }

        if (estIndex < 0) {
            dispatch(setNotification({ title: "Ocurrió un error", desc: 'No se encontró el establecimiento a editar', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }

        const currentCallers = establishments[estIndex].callers;
        const callerIndex = currentCallers.findIndex(c => c.patientcallerconfig === newCaller.patientcallerconfig);

        let updatedCallers: PatientCaller[];

        savePatientCaller(newCaller).then((res: PatientCaller) => {
            res.establishment = newCaller.establishment //le seteo el establishment que tenía, un campo logico del front
            
            if (callerIndex >= 0) { //Si existe (estaba editando), actualizo el llamador
                updatedCallers = [...currentCallers];
                updatedCallers[callerIndex] = res //en el indice del llamador a editar, le setea los datos editados del llamador
            } else {
                updatedCallers = [...currentCallers, res]; //Agrega nuevo llamador
            }

            updateCallers(estIndex, updatedCallers);
            dispatch(setNotification({ title: (editCaller && editCaller.patientcallerconfig > 0) ? "Edición exitosa":"Creación exitosa", desc: (editCaller && editCaller.patientcallerconfig > 0) ? 'Se ha editado el llamador':'Se ha creado el llamador', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            setEditCaller(null);
            setOpenModal(false);
        }).catch((err) => {
            console.error(err);
            if (err.response?.status === 409) { //Unique constarint error para callerid en Input "ID"
                dispatch(setNotification({ title: "No se puede guardar", desc: "La ID ya está siendo usada por otro llamador.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            } else
                dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo guardar el llamador`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    const establishmentsToRender = isMobile
        ? orderedEstablishments.filter(est => selectedEstablishmentIds.includes(est.buildingid))
        : orderedActiveEstablishments;

    return (
        <div>
            {isMobile ? (
                <div className="emr-border-bottom pb-2">
                    <div className="location-mobile-caller-header-text">
                        <h1 className="title2">Llamador</h1>
                        <p className="grey-color">Los llamadores se asocian a los sectores y consultorios de establecimientos creados.</p>
                    </div>
                    <div className="location-mobile-caller-actions-row"style={{fontSize:"13px"}}>
                        <Button
                            id="buttoncreatecallerid"
                            icon="pi pi-plus-circle"
                            className="buttonMain location-mobile-caller-action-btn location-mobile-caller-action-btn--full"
                            label="Crear"
                            onClick={handleCreate}
                        />
                        <Button
                            id="buttonresetcallersid"
                            icon="pi pi-refresh"
                            className="btnCancel2 location-mobile-caller-action-btn location-mobile-caller-action-btn--full"
                            label="Resetear numeradores"
                            onClick={handleResetAll}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <h1 className="title2">Llamador</h1>
                    <div className="flex flex-row justify-content-between emr-border-bottom pb-2">
                        <p className="grey-color">Los llamadores se asocian a los sectores y consultorios de establecimientos creados.</p>
                        <div className="flex gap-2">
                            <Button id="buttoncreatecallerid" icon="pi pi-plus-circle" className="buttonMain margin-create-button" label="Crear" onClick={handleCreate} />
                            <Button id="buttonresetcallersid" icon="pi pi-refresh" className="btnCancel2 margin-create-button" label="Resetear numeradores" onClick={handleResetAll} />
                        </div>
                    </div>
                </>
            )}

            {isMobile && (
                <LocationEstablishmentFilter
                    establishments={orderedEstablishments}
                    selectedEstablishmentIds={selectedEstablishmentIds}
                    onChange={setSelectedEstablishmentIds}
                />
            )}


            {establishmentsToRender.map((est) => {
                const realIndex = establishments.findIndex(x => x.buildingid === est.buildingid);
                return (
                <div key={est.buildingid} className="my-5">
                    <div style={{marginBottom: '-8px'}} className="flex flex-row gap-4">
                        <h2 className="title3">{est.buildingname}</h2>
                        <p className="grey-color mt-1">{est.buildingaddress}</p>
                    </div>
                    <PatientCallerTable
                        callers={est.callers}
                        onChange={(newCallers) => updateCallers(realIndex, newCallers)}
                        onEdit={(caller) => handleEdit(caller)}
                        onDelete={(caller) => handleOpenRemoveModal(caller)}
                    />
                    <div style={{paddingBottom: '16px'}}></div>
                </div>
                );
            })}

            {openModal && (
                <CRUDPatientCallerModal
                    visible={openModal}
                    setVisible={setOpenModal}
                    onSubmit={handleSave}
                    callerToEdit={editCaller ?? undefined}
                    allowedEstablishments={isMobile ? establishmentsToRender : undefined}
                />
            )}

            <Modal className="location-mobile-editor-modal" visible={openRemoveModal} setVisible={setOpenRemoveModal} header={"Estás por eliminar el llamador"} headerStyle={{ fontSize: '12px', textAlign: 'center' }} width='w-medium' closable={false}>
                <div>
                    <p className="grey-color">No podrás eliminar llamadores que tengan elementos multimedia asociados. Los puestos y consultorios quedarán desasociados del mismo.</p>
                    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                        <Button id="deletebuttonid" label="Si, eliminar" onClick={() => handleDelete(editCaller)} className="w-full buttonMain" />
                        <Button id="canceldeletebuttonid" label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}


